import SouthIcon from "@mui/icons-material/South";
import {
  Box,
  Container,
  Grid,
  Stack,
  Theme,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CustomButton from "global/components/CustomButton/CustomButton";
import viewpaths from "global/constants/ViewPathConstants";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { animationStyle } from "utils/styles";
import darkBGImage from "../../../assets/images/DarkBGImage.webp";
import heroImageWhite from "../../../assets/images/WhiteBGImage.jpg";
import homeStyles from "../Home.styles";

const smoothScroll = (target: any, duration: number) => {
  const start = window.pageYOffset;
  const targetPosition = target.getBoundingClientRect().top;
  const end = targetPosition + start;
  const distance = end - start - 50;
  let startTime: number | null = null;

  const animation = (currentTime: number) => {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);
    window.scrollTo(0, start + distance * easeInOutQuad(progress));
    if (timeElapsed < duration) {
      window.requestAnimationFrame(animation);
    }
  };

  // Easing function
  const easeInOutQuad = (t: number) => {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  };

  window.requestAnimationFrame(animation);
};

const handleScrollToSection = (sectionRef: React.RefObject<HTMLElement>) => {
  if (sectionRef.current) {
    smoothScroll(sectionRef.current, 2000); // 1 second duration
  }
};

const animation: any = makeStyles(animationStyle);

const HeroSection = ({
  section1Ref,
  section2Ref,
}: {
  section1Ref: React.RefObject<HTMLElement>;
  section2Ref: React.RefObject<HTMLElement>;
}) => {
  const history = useHistory();
  const muiTheme = useTheme();
  const classes = homeStyles;
  const animationClasses = animation();
  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const { t } = useTranslation();

  const highlightSpanSx = {
    position: "relative" as const,
    fontWeight: 700,
    letterSpacing: "0.03em",
    background:
      "linear-gradient(135deg, #e63900 0%, #f8481c 40%, #ff6b4a 100%)",
    backgroundSize: "200% 100%",
    backgroundClip: "text",
    WebkitBackgroundClip: "text" as const,
    WebkitTextFillColor: "transparent",
    textShadow: "none",
    // "&::before": {
    //   content: '""',
    //   position: "absolute" as const,
    //   left: "-2px",
    //   right: "-2px",
    //   bottom: "2px",
    //   height: "0.45em",
    //   background: `linear-gradient(105deg, ${alpha("#f8481c", 0.28)} 0%, ${alpha("#ff6b4a", 0.22)} 100%)`,
    //   borderRadius: "3px",
    //   zIndex: -1,
    //   animation: "highlightGlow 3s ease-in-out infinite",
    // },
  };

  return (
    <>
      <style>
        {`
          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 145% 50%;
            }
          }
          @keyframes highlightGlow {
            0%, 100% { opacity: 0.85; }
            50% { opacity: 1; }
          }
          @keyframes fadeInUpScaleLarge {
            from {
              opacity: 0;
              transform: translateY(50px) scale(0.85) perspective(1000px) rotateY(-8deg) rotateX(-2deg);
              z-index: 0;
            }
            to {
              opacity: 1;
              transform: perspective(1000px) rotateY(-5deg) rotateX(2deg);
              z-index: 2;
            }
          }
          @keyframes fadeInUpScaleSmall {
            from {
              opacity: 0;
              transform: translateY(50px) scale(0.85) perspective(1000px) rotateY(10deg) rotateX(2deg);
              z-index: 0;
            }
            to {
              opacity: 1;
              transform: perspective(1000px) rotateY(8deg) rotateX(-2deg);
              z-index: 1;
            }
          }
          @keyframes floatParticle1 {
            0%, 100% {
              transform: translateY(0) scale(1);
            }
            50% {
              transform: translateY(-20px) scale(1.2);
            }
          }
          @keyframes floatParticle2 {
            0%, 100% {
              transform: translateY(0) scale(1);
            }
            50% {
              transform: translateY(-15px) scale(1.1);
            }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 0.6;
            }
            50% {
              opacity: 1;
            }
          }
          @keyframes shimmer {
            0% {
              background-position: -1000px 0;
            }
            100% {
              background-position: 1000px 0;
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0) translateX(0) rotate(0deg);
            }
            33% {
              transform: translateY(-30px) translateX(20px) rotate(120deg);
            }
            66% {
              transform: translateY(20px) translateX(-20px) rotate(240deg);
            }
          }
          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes moveDiagonal {
            0% {
              transform: translate(0, 0) rotate(0deg);
            }
            50% {
              transform: translate(100px, 100px) rotate(180deg);
            }
            100% {
              transform: translate(0, 0) rotate(360deg);
            }
          }
          @keyframes wave {
            0%, 100% {
              transform: translateX(0) translateY(0);
            }
            25% {
              transform: translateX(50px) translateY(-20px);
            }
            50% {
              transform: translateX(100px) translateY(0);
            }
            75% {
              transform: translateX(50px) translateY(20px);
            }
          }
          @keyframes scalePulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.4;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.8;
            }
          }
          @keyframes blurInUp {
            from {
              opacity: 0;
              transform: translateY(40px) scale(0.9);
              filter: blur(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0);
            }
          }
          @keyframes slideRotateIn {
            from {
              opacity: 0;
              transform: translateX(-60px) translateY(30px) rotate(-15deg) scale(0.8);
            }
            to {
              opacity: 1;
              transform: translateX(0) translateY(0) rotate(0deg) scale(1);
            }
          }
          @keyframes scaleBounce {
            0% {
              opacity: 0;
              transform: scale(0.3) rotate(-10deg);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.1) rotate(5deg);
            }
            70% {
              transform: scale(0.95) rotate(-2deg);
            }
            100% {
              opacity: 1;
              transform: scale(1) rotate(0deg);
            }
          }
          @keyframes morphIn {
            0% {
              opacity: 0;
              transform: scale(0.5) rotate(180deg);
              border-radius: 50%;
            }
            50% {
              opacity: 0.7;
              transform: scale(1.1) rotate(90deg);
              border-radius: 30%;
            }
            100% {
              opacity: 1;
              transform: scale(1) rotate(0deg);
              border-radius: 8px;
            }
          }
          @keyframes revealFromLeft {
            from {
              opacity: 0;
              transform: translateX(-100px) scaleX(0);
              transform-origin: left;
            }
            to {
              opacity: 1;
              transform: translateX(0) scaleX(1);
              transform-origin: left;
            }
          }
          @keyframes revealFromRight {
            from {
              opacity: 0;
              transform: translateX(100px) scaleX(0);
              transform-origin: right;
            }
            to {
              opacity: 1;
              transform: translateX(0) scaleX(1);
              transform-origin: right;
            }
          }
          @keyframes zoomInRotate {
            from {
              opacity: 0;
              transform: scale(0.5) rotate(-20deg) translateY(50px);
            }
            to {
              opacity: 1;
              transform: scale(1) rotate(0deg) translateY(0);
            }
          }
          @keyframes elasticPop {
            0% {
              opacity: 0;
              transform: scale(0) rotate(180deg);
            }
            50% {
              opacity: 1;
              transform: scale(1.2) rotate(90deg);
            }
            70% {
              transform: scale(0.9) rotate(45deg);
            }
            100% {
              opacity: 1;
              transform: scale(1) rotate(0deg);
            }
          }
          @keyframes slideUpFade {
            from {
              opacity: 0;
              transform: translateY(60px) scale(0.95);
              filter: blur(5px);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0);
            }
          }
          @keyframes rotateInScale {
            from {
              opacity: 0;
              transform: rotate(-180deg) scale(0.5);
            }
            to {
              opacity: 1;
              transform: rotate(0deg) scale(1);
            }
          }
          @keyframes textReveal {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
              clip-path: inset(0 100% 0 0);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
              clip-path: inset(0 0% 0 0);
            }
          }
          @keyframes textSlideIn {
            from {
              opacity: 0;
              transform: translateX(-50px) translateY(20px);
              filter: blur(8px);
            }
            to {
              opacity: 1;
              transform: translateX(0) translateY(0);
              filter: blur(0);
            }
          }
          @keyframes textGlowIn {
            0% {
              opacity: 0;
              transform: scale(0.8);
              text-shadow: 0 0 0px rgba(248, 72, 28, 0);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
              text-shadow: 0 0 20px rgba(248, 72, 28, 0.5);
            }
            100% {
              opacity: 1;
              transform: scale(1);
              text-shadow: 0 0 0px rgba(248, 72, 28, 0);
            }
          }
          @keyframes wordReveal {
            from {
              opacity: 0;
              transform: translateY(20px) rotateX(90deg);
            }
            to {
              opacity: 1;
              transform: translateY(0) rotateX(0deg);
            }
          }
          @keyframes textWave {
            0%, 100% {
              transform: translateY(0);
            }
            25% {
              transform: translateY(-5px);
            }
            75% {
              transform: translateY(5px);
            }
          }
          @keyframes shimmerText {
            0% {
              background-position: -200% center;
            }
            100% {
              background-position: 200% center;
            }
          }
          @keyframes boxReveal {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(30px);
              clip-path: polygon(0 0, 0 0, 0 100%, 0% 100%);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
              clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
            }
          }
          @keyframes cornerAccentReveal {
            from {
              opacity: 0;
              transform: scale(0) rotate(-45deg);
            }
            to {
              opacity: 1;
              transform: scale(1) rotate(0deg);
            }
          }
          @keyframes buttonSlideIn {
            from {
              opacity: 0;
              transform: translateX(-40px) scale(0.9);
              filter: blur(5px);
            }
            to {
              opacity: 1;
              transform: translateX(0) scale(1);
              filter: blur(0);
            }
          }
          @keyframes accentLineDraw {
            from {
              width: 0;
              opacity: 0;
            }
            to {
              width: 40px;
              opacity: 0.6;
            }
          }
        `}
      </style>
      <Box
        sx={{
          ...classes.backImg,
          backgroundColor: muiTheme.palette.background.default,
          background:
            muiTheme.palette.mode === "dark"
              ? `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${darkBGImage})`
              : `url(${heroImageWhite})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
        ref={section1Ref}
      >
        {/* Decorative Background Elements */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {/* Gradient Orbs */}
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              right: "15%",
              width: { md: "300px", xs: "150px" },
              height: { md: "300px", xs: "150px" },
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(
                "#f8481c",
                muiTheme.palette.mode === "dark" ? 0.15 : 0.08
              )} 0%, transparent 70%)`,
              filter: "blur(40px)",
              animation: "pulse 4s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "20%",
              left: "10%",
              width: { md: "250px", xs: "120px" },
              height: { md: "250px", xs: "120px" },
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(
                "#f8481c",
                muiTheme.palette.mode === "dark" ? 0.12 : 0.06
              )} 0%, transparent 70%)`,
              filter: "blur(35px)",
              animation: "pulse 5s ease-in-out infinite 1s",
            }}
          />
          {/* Subtle Grid Pattern */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `linear-gradient(${alpha(
                muiTheme.palette.divider,
                0.03
              )} 1px, transparent 1px), linear-gradient(90deg, ${alpha(
                muiTheme.palette.divider,
                0.03
              )} 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
              opacity: 0.5,
            }}
          />

          {/* Animated Geometric Shapes */}
          {[...Array(6)].map((_, i) => (
            <Box
              key={`shape-${i}`}
              sx={{
                position: "absolute",
                width: { md: `${60 + i * 20}px`, xs: `${40 + i * 15}px` },
                height: { md: `${60 + i * 20}px`, xs: `${40 + i * 15}px` },
                borderRadius: i % 2 === 0 ? "50%" : "10%",
                background: `linear-gradient(135deg, ${alpha(
                  "#f8481c",
                  0.08 + i * 0.02
                )} 0%, ${alpha("#f8481c", 0.03 + i * 0.01)} 100%)`,
                border: `1px solid ${alpha("#f8481c", 0.15)}`,
                top: `${10 + i * 15}%`,
                left: i % 2 === 0 ? `${5 + i * 8}%` : "auto",
                right: i % 2 === 1 ? `${5 + (i - 1) * 8}%` : "auto",
                animation: `float ${8 + i * 2}s ease-in-out infinite ${
                  i * 0.5
                }s`,
                filter: "blur(1px)",
              }}
            />
          ))}

          {/* Animated Rotating Rings */}
          {[...Array(3)].map((_, i) => (
            <Box
              key={`ring-${i}`}
              sx={{
                position: "absolute",
                width: { md: `${200 + i * 100}px`, xs: `${150 + i * 50}px` },
                height: { md: `${200 + i * 100}px`, xs: `${150 + i * 50}px` },
                border: `2px dashed ${alpha("#f8481c", 0.1 + i * 0.05)}`,
                borderRadius: "50%",
                top: `${20 + i * 25}%`,
                left: `${15 + i * 10}%`,
                animation: `rotate ${20 + i * 5}s linear infinite ${i * 2}s`,
                opacity: 0.4,
              }}
            />
          ))}

          {/* Animated Floating Dots */}
          {[...Array(8)].map((_, i) => (
            <Box
              key={`dot-${i}`}
              sx={{
                position: "absolute",
                width: `${6 + (i % 3) * 2}px`,
                height: `${6 + (i % 3) * 2}px`,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(
                  "#f8481c",
                  0.6
                )} 0%, ${alpha("#f8481c", 0.2)} 100%)`,
                top: `${15 + i * 12}%`,
                left: `${10 + (i % 4) * 25}%`,
                animation: `wave ${4 + (i % 3)}s ease-in-out infinite ${
                  i * 0.3
                }s`,
                boxShadow: `0 0 ${8 + i * 2}px ${alpha("#f8481c", 0.4)}`,
              }}
            />
          ))}

          {/* Animated Gradient Lines */}
          <Box
            sx={{
              position: "absolute",
              top: "30%",
              left: "0%",
              width: "100%",
              height: "2px",
              background: `linear-gradient(90deg, transparent 0%, ${alpha(
                "#f8481c",
                0.3
              )} 50%, transparent 100%)`,
              animation: "moveDiagonal 15s ease-in-out infinite",
              transform: "rotate(-5deg)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "70%",
              left: "0%",
              width: "100%",
              height: "2px",
              background: `linear-gradient(90deg, transparent 0%, ${alpha(
                "#f8481c",
                0.2
              )} 50%, transparent 100%)`,
              animation: "moveDiagonal 18s ease-in-out infinite 2s",
              transform: "rotate(5deg)",
            }}
          />

          {/* Pulsing Circles */}
          {[...Array(4)].map((_, i) => (
            <Box
              key={`pulse-${i}`}
              sx={{
                position: "absolute",
                width: { md: `${80 + i * 40}px`, xs: `${60 + i * 30}px` },
                height: { md: `${80 + i * 40}px`, xs: `${60 + i * 30}px` },
                borderRadius: "50%",
                border: `2px solid ${alpha("#f8481c", 0.2 - i * 0.03)}`,
                top: `${25 + i * 20}%`,
                right: `${10 + i * 15}%`,
                animation: `scalePulse ${3 + i}s ease-in-out infinite ${
                  i * 0.5
                }s`,
              }}
            />
          ))}
        </Box>

        <Container
          maxWidth="xl"
          sx={{ position: "relative", zIndex: 1, top: 40 }}
        >
          <Grid container spacing={4} sx={{ py: { md: 4, xs: 3 } }}>
            {/* Text Section - Larger (7 columns) */}
            <Grid item xl={7} lg={7} md={7} sm={12} xs={12}>
              <Stack
                direction={"column"}
                spacing={2.5}
                className={animationClasses.fadeInDown}
                sx={{ height: "100%", justifyContent: "center" }}
              >
                <Typography
                  sx={{
                    ...classes.headerText,
                    color: muiTheme.palette.text.primary,
                    background:
                      muiTheme.palette.mode === "dark"
                        ? "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 65%, #f8481c 85%, #ff6b4a 100%)"
                        : "linear-gradient(135deg, #000000 0%, #000000 65%, #f8481c 85%, #ff6b4a 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundSize: "300% 300%",
                    animation:
                      "gradientShift 5s ease infinite, textReveal 1.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both, textGlowIn 1.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.1s both",
                    mb: 1,
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-8px",
                      left: 0,
                      width: "0%",
                      height: "3px",
                      background:
                        "linear-gradient(90deg, #f8481c 0%, #ff6b4a 100%)",
                      animation:
                        "accentLineDraw 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 1.2s both",
                    },
                  }}
                >
                  {t("heroHeader")}
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    maxWidth: "650px",
                    animation:
                      "boxReveal 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s both",
                    p: { xs: 2, md: 3 },
                    borderRadius: "8px",
                    background:
                      muiTheme.palette.mode === "dark"
                        ? alpha(muiTheme.palette.background.paper, 0.3)
                        : alpha(muiTheme.palette.background.paper, 0.5),
                    border: `1px solid ${alpha(muiTheme.palette.divider, 0.2)}`,
                    backdropFilter: "blur(10px)",
                    boxShadow:
                      muiTheme.palette.mode === "dark"
                        ? `0 8px 32px ${alpha(
                            "#000000",
                            0.3
                          )}, inset 0 1px 0 ${alpha("#FFFFFF", 0.05)}`
                        : `0 8px 32px ${alpha(
                            "#000000",
                            0.08
                          )}, inset 0 1px 0 ${alpha("#FFFFFF", 0.8)}`,
                  }}
                >
                  {/* Decorative corner accent */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "60px",
                      height: "60px",
                      borderTop: `3px solid ${alpha("#f8481c", 0.6)}`,
                      borderLeft: `3px solid ${alpha("#f8481c", 0.6)}`,
                      borderTopLeftRadius: "16px",
                      animation:
                        "cornerAccentReveal 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.8s both",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      width: "40px",
                      height: "40px",
                      borderBottom: `2px solid ${alpha("#f8481c", 0.4)}`,
                      borderRight: `2px solid ${alpha("#f8481c", 0.4)}`,
                      borderBottomRightRadius: "16px",
                      animation:
                        "cornerAccentReveal 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) 1s both",
                    }}
                  />

                  <Typography
                    sx={{
                      ...classes.subHeaderText,
                      fontSize: { xs: "15px", md: "17px" },
                      lineHeight: 1.8,
                      color: muiTheme.palette.text.primary,
                      fontWeight: 400,
                      position: "relative",
                      animation:
                        "textSlideIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.9s both",
                      "&::first-letter": {
                        fontSize: "1.6em",
                        fontWeight: 700,
                        background:
                          "linear-gradient(135deg, #f8481c 0%, #ff6b4a 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        lineHeight: 1,
                        marginRight: "5px",
                        color: "#f8481c",
                        animation:
                          "textGlowIn 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.9s both",
                      },
                    }}
                  >
                    The day we begin to focus on our{" "}
                    <Box component="span" sx={highlightSpanSx}>
                      moral obligation
                    </Box>
                    , our{" "}
                    <Box component="span" sx={highlightSpanSx}>
                      OWN strength
                    </Box>
                    ,{" "}
                    <Box component="span" sx={highlightSpanSx}>
                      positivity
                    </Box>{" "}
                    and{" "}
                    <Box component="span" sx={highlightSpanSx}>
                      solutions
                    </Box>
                    , it will make more{" "}
                    <Box component="span" sx={highlightSpanSx}>
                      progress
                    </Box>{" "}
                    and bring more{" "}
                    <Box component="span" sx={highlightSpanSx}>
                      prosperity
                    </Box>{" "}
                    in{" "}
                    <Box component="span" sx={highlightSpanSx}>
                      one decade
                    </Box>{" "}
                    than in all the previous centuries of our existence.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    animation:
                      "buttonSlideIn 1.1s cubic-bezier(0.34, 1.56, 0.64, 1) 1.1s both",
                    mt: { xs: 1, md: 0.5 },
                  }}
                >
                  <Stack
                    direction={{ sm: "row", xs: "column" }}
                    spacing={1.5}
                    alignItems={{ sm: "flex-start", xs: "center" }}
                  >
                    <Box
                      sx={{
                        animation:
                          "scaleBounce 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) 1.1s both",
                      }}
                    >
                      <CustomButton
                        label={"Our Blogs"}
                        buttonType="gradientBtn"
                        onClick={() => history.push(viewpaths.blogsViewPath)}
                        customClasses={classes.ourServiceBtn}
                      />
                    </Box>
                    {/* Decorative accent */}
                    <Box
                      sx={{
                        display: { xs: "none", sm: "flex" },
                        alignItems: "center",
                        gap: 1,
                        ml: 2,
                        opacity: 0.6,
                        animation: "fadeInUp 0.8s ease-out 1.4s both",
                      }}
                    >
                      <Box
                        sx={{
                          width: "0px",
                          height: "2px",
                          background: `linear-gradient(90deg, ${alpha(
                            "#f8481c",
                            0.6
                          )} 0%, transparent 100%)`,
                          animation:
                            "accentLineDraw 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 1.4s both",
                        }}
                      />
                      <Box
                        sx={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: alpha("#f8481c", 0.4),
                          animation:
                            "pulse 2s ease-in-out infinite, elasticPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 1.6s both",
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    animation:
                      "slideRotateIn 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 1.5s both",
                    mt: 1,
                  }}
                >
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems={"center"}
                    onClick={() => handleScrollToSection(section2Ref)}
                    sx={classes.scrollDownButton}
                    className="hvr-bounce-in"
                  >
                    <Box
                      sx={{
                        ...classes.downArrowWrapper,
                        borderColor: alpha("#f8481c", 0.4),
                        animation:
                          "rotateInScale 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) 1.5s both",
                        "&:hover": {
                          borderColor: "#f8481c",
                          backgroundColor: alpha("#f8481c", 0.1),
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <SouthIcon
                        sx={{
                          fontSize: "20px",
                          color: "#f8481c",
                          animation: "textWave 2s ease-in-out infinite 2s",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        ...classes.scrollDownText,
                        color: muiTheme.palette.text.secondary,
                        animation:
                          "textSlideIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 1.7s both",
                      }}
                    >
                      {t("scrollDown")}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Grid>
            {/* Image Section - Responsive Clickable Cards */}
            <Grid item xl={5} lg={5} md={5} sm={12} xs={12}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                sx={{
                  py: { xs: 2, md: 0 },
                  justifyContent: { md: "center", xs: "center" },
                  alignItems: "center",
                  animation:
                    "revealFromRight 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s both",
                }}
              >
                {[
                  {
                    src: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=800&fit=crop",
                    title: "Panacea Project",
                    description: "Become a Panacea Passport Holder",
                    url: "https://panaceaproject.com/",
                    size: "large",
                  },
                  {
                    src: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=800&fit=crop",
                    title: "IIS USA",
                    description: "Impacting the World",
                    url: "https://iisusa.org/",
                    size: "small",
                  },
                ].map((image, index) => (
                  <Box
                    key={index}
                    className="hero-image-container"
                    onClick={() =>
                      window.open(image.url, "_blank", "noopener,noreferrer")
                    }
                    sx={{
                      position: "relative",
                      width: {
                        xs: "100%",
                        sm: image.size === "large" ? "320px" : "280px",
                        md: image.size === "large" ? "360px" : "300px",
                      },
                      height: {
                        xs: image.size === "large" ? "400px" : "350px",
                        sm: image.size === "large" ? "420px" : "380px",
                        md: image.size === "large" ? "480px" : "380px",
                      },
                      overflow: "hidden",
                      borderRadius: "8px",
                      cursor: "pointer",
                      border:
                        muiTheme.palette.mode === "dark"
                          ? "2px solid rgba(255, 255, 255, 0.2)"
                          : "none",
                      boxShadow:
                        muiTheme.palette.mode === "dark"
                          ? image.size === "large"
                            ? "0 20px 50px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.15), 0 0 30px rgba(255, 255, 255, 0.1)"
                            : "0 15px 40px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.15), 0 0 25px rgba(255, 255, 255, 0.08)"
                          : image.size === "large"
                          ? "0 20px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)"
                          : "0 15px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                      transition:
                        "all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1), z-index 0.3s ease",
                      transform:
                        image.size === "large"
                          ? "perspective(1000px) rotateY(-5deg) rotateX(2deg)"
                          : "perspective(1000px) rotateY(8deg) rotateX(-2deg)",
                      transformOrigin: "center center",
                      willChange: "transform, z-index",
                      zIndex: index === 0 ? 2 : 1,
                      backfaceVisibility: "hidden",
                      pointerEvents: "auto",
                      animation:
                        index === 0
                          ? "zoomInRotate 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both"
                          : "morphIn 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.8s both",
                      "&:hover": {
                        transform:
                          image.size === "large"
                            ? "translateY(-25px) scale(1.05) perspective(1000px) rotateY(-2deg) rotateX(5deg)"
                            : "translateY(-20px) scale(1.08) perspective(1000px) rotateY(5deg) rotateX(3deg)",
                        border:
                          muiTheme.palette.mode === "dark"
                            ? "2px solid rgba(255, 255, 255, 0.3)"
                            : "none",
                        boxShadow:
                          muiTheme.palette.mode === "dark"
                            ? image.size === "large"
                              ? "0 35px 70px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.25), 0 0 40px rgba(255, 255, 255, 0.15)"
                              : "0 30px 60px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.25), 0 0 35px rgba(255, 255, 255, 0.12)"
                            : image.size === "large"
                            ? "0 35px 70px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1)"
                            : "0 30px 60px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.1)",
                        zIndex: 10,
                        "& .image-overlay": {
                          opacity: 1,
                          background:
                            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.65) 100%)",
                        },
                        "& .hero-image": {
                          transform: "scale(1.15) rotate(3deg)",
                          filter: "brightness(0.9) saturate(1.1)",
                          opacity: 1,
                        },
                        "& .content-wrapper": {
                          transform: "translateY(0) !important",
                          opacity: "1 !important",
                        },
                        "& .title-text": {
                          transform: "translateY(0) !important",
                          opacity: "1 !important",
                        },
                        "& .description-text": {
                          transform: "translateY(0) !important",
                          opacity: "1 !important",
                          transitionDelay: "0.2s",
                        },
                        "& .shine-effect": {
                          left: "100%",
                        },
                        "& .border-glow": {
                          opacity: 1,
                          boxShadow:
                            muiTheme.palette.mode === "dark"
                              ? "0 0 40px rgba(248, 72, 28, 0.6), inset 0 0 40px rgba(248, 72, 28, 0.2), 0 0 60px rgba(248, 72, 28, 0.3)"
                              : "0 0 40px rgba(248, 72, 28, 0.5), inset 0 0 40px rgba(248, 72, 28, 0.15), 0 0 60px rgba(248, 72, 28, 0.25)",
                        },
                        "& .corner-accent": {
                          opacity: 1,
                          background:
                            "linear-gradient(135deg, rgba(248, 72, 28, 0.6) 0%, rgba(248, 72, 28, 0.3) 50%, transparent 100%)",
                        },
                        "& .floating-particles": {
                          opacity: 1,
                        },
                        "& .orange-overlay": {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    {/* Image - Base Layer */}
                    <Box
                      component="img"
                      src={image.src}
                      alt={image.title}
                      className="hero-image"
                      sx={{
                        ...classes.heroImageElement,
                        ...(muiTheme.palette.mode === "dark"
                          ? classes.heroImageElementDark
                          : {}),
                      }}
                    />
                    {/* Gradient Overlay - Always Visible */}
                    <Box
                      className="image-overlay"
                      sx={{
                        ...classes.imageOverlay,
                        opacity: 1,
                        background:
                          "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)",
                      }}
                    >
                      {/* Content Wrapper - Always Visible */}
                      <Box
                        className="content-wrapper"
                        sx={{
                          ...classes.contentWrapper,
                          transform: "translateY(0)",
                          opacity: 1,
                        }}
                      >
                        {/* Title Text */}
                        <Typography
                          className="title-text"
                          sx={{
                            ...classes.titleText,
                            transform: "translateY(0)",
                            opacity: 1,
                          }}
                        >
                          {image.title}
                        </Typography>
                        {/* Description Text - Single Line */}
                        <Typography
                          className="description-text"
                          sx={{
                            ...classes.descriptionText,
                            transform: "translateY(0)",
                            opacity: 1,
                          }}
                        >
                          {image.description}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Animated Border Glow */}
                    <Box className="border-glow" sx={classes.borderGlow} />
                    {/* Shine Effect - Sweeps across on hover with orange */}
                    <Box
                      className="shine-effect"
                      sx={{
                        ...classes.shineEffect,
                        background: `linear-gradient(90deg, transparent 0%, ${alpha(
                          "#f8481c",
                          0.4
                        )} 30%, ${alpha("#f8481c", 0.6)} 50%, ${alpha(
                          "#f8481c",
                          0.4
                        )} 70%, transparent 100%)`,
                      }}
                    />
                    {/* Corner Accent */}
                    <Box className="corner-accent" sx={classes.cornerAccent} />
                    {/* Orange Gradient Overlay on Hover */}
                    <Box
                      className="orange-overlay"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${alpha(
                          "#f8481c",
                          0
                        )} 0%, ${alpha("#f8481c", 0.08)} 50%, ${alpha(
                          "#f8481c",
                          0
                        )} 100%)`,
                        opacity: 0,
                        transition: "opacity 0.6s ease",
                        borderRadius: "8px",
                        zIndex: 2,
                        pointerEvents: "none",
                      }}
                    />
                    {/* Floating Particles Effect */}
                    <Box
                      className="floating-particles"
                      sx={classes.floatingParticle1}
                    />
                    <Box
                      className="floating-particles"
                      sx={classes.floatingParticle2}
                    />
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default HeroSection;
